






























































































































import {
  defineComponent,
  computed,
  ref,
  watch,
  PropType
} from '@vue/composition-api';
import { cloneDeep } from 'lodash';
import { AxiosError } from 'axios';

import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import {
  useCreateTariffException,
  useUpdateTariffException
} from '@/composable/tariff-exceptions';
import { ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';
import { percentageValidator, requiredValidator } from '@/utils/validators';
import {
  ITariffExceptionData,
  ITariffException,
  TariffException
} from '@/models';

import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import CountryPicker from '@/components/common/CountryPicker.vue';
import CSIItemSearch from '../csi/ItemSearch.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import HSCodeAutoCompletePicker from '@/components/harmonized-system/HSCodeAutoCompletePicker.vue';

export default defineComponent({
  name: 'TariffExceptionFormDialog',

  components: {
    ALDialog,
    ErrorAlert,
    CountryPicker,
    CSIItemSearch,
    HSCodeAutoCompletePicker
  },

  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    tariffException: {
      type: Object as PropType<ITariffException | null>,
      default: null
    }
  },
  emits: ['update:dialog'],

  setup(props, { emit }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const tariffExceptionData = ref<ITariffExceptionData>({});

    watch(localDialog, value => {
      if (value) {
        resetFormValidation();
        tariffExceptionData.value = !props.isEdit
          ? ({
              countries: []
            } as ITariffExceptionData)
          : cloneDeep(props.tariffException!);
      }
    });

    const formRef = ref<ALForm | null>(null);
    const valid = ref(false);

    const subtitle = computed(
      () => `${props.isEdit ? 'Update' : 'Create'} tariff exception information`
    );

    function closeDialog() {
      clearErrors();
      localDialog.value = false;
    }

    const {
      isLoading: isUpdatePending,
      error: updateError,
      mutate: updateTariffException,
      reset: updateReset
    } = useUpdateTariffException();
    const {
      isLoading: isCreatePending,
      error: createError,
      mutate: createTariffException,
      reset: createReset
    } = useCreateTariffException();

    const localError = ref<ISystemError | null>(null);

    const loading = computed(() =>
      props.isEdit ? isUpdatePending.value : isCreatePending.value
    );

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = props.isEdit ? updateError.value : createError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    function clearErrors() {
      props.isEdit ? updateReset() : createReset();
      error.value = null;
    }

    function resetFormValidation(): void {
      if (formRef.value) {
        return formRef.value.resetValidation();
      }
    }

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    async function save() {
      clearErrors();
      const valid = await validateForm();

      if (valid) {
        let tariffExceptionInstance: ITariffException | null = null;

        try {
          tariffExceptionInstance = new TariffException(
            tariffExceptionData.value
          );
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (tariffExceptionInstance != null) {
          const saveAction = props.isEdit
            ? updateTariffException
            : createTariffException;

          let payload = tariffExceptionInstance;
          if (props.isEdit) {
            payload = diffDeep(
              tariffExceptionInstance,
              props.tariffException,
              true
            );
          }

          saveAction(payload, {
            onSuccess: () => {
              closeDialog();
            }
          });
        }
      }
    }

    return {
      localDialog,
      tariffExceptionData,
      valid,
      formRef,
      subtitle,
      closeDialog,
      save,
      clearErrors,
      loading,
      error,
      requiredValidator,
      percentageValidator
    };
  }
});
